import React, { useCallback, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { seoFetchService } from '@/services/seoFetchService';
import { seoDbService } from '@/services/seoDbService';
import { CheckCircle2, Code2, FileJson2, Loader2, TriangleAlert } from 'lucide-react';

interface FoundSchema {
  type: string;
  content: any;
}

const SchemaValidator: React.FC = () => {
  const [url, setUrl] = useState('https://seniorthrive.com');
  const [isLoading, setIsLoading] = useState(false);
  const [schemas, setSchemas] = useState<FoundSchema[]>([]);

  const validate = useCallback(async () => {
    try {
      setIsLoading(true);
      setSchemas([]);
      const { html, url: finalUrl } = await seoFetchService.fetchHtml(url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const jsonLdScripts = Array.from(doc.querySelectorAll('script[type="application/ld+json"]'));

      const found: FoundSchema[] = [];
      for (const s of jsonLdScripts) {
        try {
          const json = JSON.parse(s.textContent || '{}');
          const items = Array.isArray(json) ? json : [json];
          items.forEach(item => {
            const type = item['@type'] || 'Unknown';
            found.push({ type, content: item });
          });
        } catch (e) {
          // ignore parse error for this block
        }
      }

      setSchemas(found);

      // Basic validation rules
      for (const item of found) {
        const hasContext = Boolean(item.content['@context']);
        const hasType = Boolean(item.content['@type']);
        const status: 'valid' | 'invalid' | 'warning' = (!hasContext && !hasType)
          ? 'invalid'
          : (!hasContext || !hasType)
          ? 'warning'
          : 'valid';

        await seoDbService.saveSchemaValidation({
          url: finalUrl,
          schema_type: String(item.content['@type'] || 'Unknown'),
          schema_content: item.content,
          validation_status: status,
          validation_errors: status === 'invalid' ? [{ message: 'Missing @context and @type' }] : [],
          validation_warnings: status === 'warning' ? [{ message: 'Schema missing @context or @type' }] : [],
          google_structured_data_test: null,
        });
      }

      toast.success('Schema validation saved', { description: `${found.length} JSON-LD blocks processed` });
    } catch (e: any) {
      console.error(e);
      toast.error('Validation failed', { description: e?.message || 'Unexpected error' });
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><FileJson2 className="h-5 w-5" /> Schema Validator</CardTitle>
          <CardDescription>Detect and validate JSON-LD structured data, then store results</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row items-center gap-3">
            <div className="grid gap-1 w-full">
              <Label htmlFor="schema-url">URL to validate</Label>
              <Input id="schema-url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://seniorthrive.com" />
            </div>
            <Button onClick={validate} disabled={isLoading} className="gap-2">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {isLoading ? 'Validating…' : 'Validate & Save'}
            </Button>
          </div>

          {schemas.length > 0 && (
            <div className="grid gap-3">
              {schemas.map((s, idx) => (
                <div key={idx} className="rounded-lg border p-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Code2 className="h-4 w-4" /> {s.type}
                  </div>
                  <pre className="mt-2 text-xs overflow-auto max-h-64">
                    {JSON.stringify(s.content, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SchemaValidator;
